// Duplicate-company detection for Lead capture.
//
// Deliberately EXACT-name only: typing "Textile Valley" flags an existing
// "Textile Valley" lead, but must NOT flag "Thagamman Textile P Ltd" or
// "arun textile" just because they share the word "textile". Word-overlap and
// fuzzy matching were tried and produced exactly that noise, so the only thing
// treated as a match is the same name.
//
// "Same name" still ignores the differences that never distinguish a company:
// letter case, punctuation, extra whitespace, and the legal-entity suffix — so
// "Textile Valley", "textile valley", and "Textile Valley Pvt. Ltd." are one
// company, while any different name is not a match at all.
import { Lead } from '@/types'

// Legal-entity suffixes stripped before comparison so "Tata Consultancy" and
// "Tata Consultancy Pvt Ltd" are treated as the same core company name.
// Sorted longest-first so multi-word suffixes ("private limited") are tried
// before shorter ones ("limited") that would otherwise match a substring of them.
const SUFFIX_WORDS = [
  'private limited', 'pvt ltd', 'pvt ltd.', 'p ltd',
  'limited', 'ltd', 'llp', 'inc', 'incorporated',
  'company', 'co', 'corporation', 'corp',
].sort((a, b) => b.length - a.length)

/**
 * Automatically abbreviate/standardize legal entity suffixes in Company Name:
 * - "Private Limited" / "Private Liumited" / "Pvt Ltd" / "Pvt. Ltd." -> "P Ltd"
 * - "Limited Liability Partnership" / "Limited Liability Patnership" -> "LLP"
 * - "Limited" -> "Ltd"
 */
export function standardizeCompanyName(name: string): string {
  if (!name) return ''
  let str = name.trim()

  // 1. Replace Limited Liability Partnership / Patnership with LLP
  str = str.replace(/\bLimited\s+Liability\s+Pat?nership\b/gi, 'LLP')

  // 2. Replace Private Limited / Private Liumited / Pvt Ltd / Pvt. Ltd. / Private Ltd / P. Ltd. / P.Ltd. with P Ltd
  str = str.replace(/\b(Private\s+Liumited|Private\s+Limited|Private\s+Ltd|Pvt\.?\s*Ltd\.?|P\.?\s*Ltd\.?)\b/gi, 'P Ltd')

  // 3. Replace standalone "Limited" (not preceded by Private/Pvt) with "Ltd"
  str = str.replace(/\bLimited\b/gi, 'Ltd')

  return str.replace(/\s+/g, ' ').trim()
}

/** Lowercase, strip punctuation, collapse whitespace. */
function clean(name: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/[.,()&/\\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Lowercased core name with trailing legal-entity suffix words repeatedly stripped. */
export function coreCompanyName(name: string): string {
  let value = clean(name)
  let changed = true
  while (changed && value) {
    changed = false
    for (const suffix of SUFFIX_WORDS) {
      if (value === suffix) { value = ''; changed = true; break }
      if (value.endsWith(' ' + suffix)) {
        value = value.slice(0, value.length - suffix.length - 1).trim()
        changed = true
        break
      }
    }
  }
  return value
}

/** True when two company names refer to the same company by name alone. */
export function isSameCompanyName(a: string, b: string): boolean {
  const coreA = coreCompanyName(a)
  return !!coreA && coreA === coreCompanyName(b)
}

/**
 * Leads whose company name is the SAME as `input`. Normally 0 or 1 result,
 * but more than one lead can legitimately already carry the same name, so
 * every match is returned for the user to pick from.
 *
 * Called on every keystroke (debounced by the caller) so it must stay cheap —
 * fine for the lead-list sizes this CRM deals with.
 */
export function findSameNameCompanies(input: string, leads: Lead[], excludeId?: string): Lead[] {
  const inputCore = coreCompanyName(input)
  if (!inputCore) return []
  return leads.filter(
    (lead) => lead.id !== excludeId && coreCompanyName(lead.companyName || '') === inputCore
  )
}
